import React from 'react';

import QuestionWrapper from '../../core/ui/question_wrapper';
import QuestionHeading from '../../core/ui/question_heading';
import QuestionSubheading from '../../core/ui/question_subheading';

import styles from './star_rating_question.module.css';

const StarIcon = ({ filled }) => (
  <svg width='40' height='38' viewBox='0 0 36 33' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path
      d='M18.9234 28.3146C18.352 28.0224 17.6751 28.0221 17.1035 28.3138L9.539 32.1742C8.06831 32.9248 6.37305 31.6707 6.6604 30.0447L8.07607 22.0342C8.19318 21.3716 7.96924 20.6945 7.48014 20.2324L1.52181 14.6027C0.303342 13.4515 0.956774 11.402 2.61675 11.1685L10.942 9.99732C11.5886 9.90635 12.1502 9.50539 12.4461 8.92327L16.2172 1.50638C16.9586 0.0483107 19.0414 0.0483135 19.7828 1.50638L23.5539 8.92327C23.8498 9.50539 24.4114 9.90635 25.058 9.99732L33.3833 11.1685C35.0432 11.402 35.6967 13.4515 34.4782 14.6027L28.5199 20.2324C28.0308 20.6945 27.8068 21.3716 27.9239 22.0342L29.3388 30.0402C29.6263 31.6669 27.9293 32.9211 26.4586 32.1688L18.9234 28.3146Z'
      fill={filled ? '#fab02f' : 'none'}
      stroke='#fab02f'
      strokeWidth='1.5'
    />
  </svg>
);

// Renders a single-select question as a row of stars, one per option in order
// (first option = 1 star). The chosen option's value is what gets recorded, so
// this is purely a display alternative to pills. Next stays disabled until a
// star is chosen unless the question is optional.
const StarRatingQuestion = ({
  heading,
  subHeading,
  options,
  value,
  onChange,
  onSubmit,
  onBack,
  current,
  total,
  submitDisabledWhenEmpty = true,
}) => {
  const selectedIndex = options.findIndex((option) => option.value === value);
  const isDisabled = submitDisabledWhenEmpty && selectedIndex < 0;

  const onStarKeyDown = (event) => {
    const { key } = event;
    const currentIndex = Number(event.currentTarget.getAttribute('data-index'));
    const lastIndex = options.length - 1;
    let nextIndex = null;
    if (key === 'ArrowRight' || key === 'ArrowDown') nextIndex = Math.min(lastIndex, currentIndex + 1);
    if (key === 'ArrowLeft' || key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 1);
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = lastIndex;
    if (nextIndex === null) return;
    event.preventDefault();
    onChange(options[nextIndex].value);
  };

  return (
    <QuestionWrapper current={current} total={total} onSubmit={onSubmit} onBack={onBack} isDisabled={isDisabled}>
      <QuestionHeading>{heading}</QuestionHeading>
      {subHeading && <QuestionSubheading>{subHeading}</QuestionSubheading>}
      <div
        className={styles.stars}
        role='radiogroup'
        aria-label={typeof heading === 'string' ? heading : 'Star rating'}
      >
        {options.map((option, index) => (
          <button
            key={option.value}
            type='button'
            className={styles.star}
            role='radio'
            aria-checked={selectedIndex === index}
            aria-label={`${index + 1} of ${options.length}`}
            tabIndex={selectedIndex === -1 ? (index === 0 ? 0 : -1) : selectedIndex === index ? 0 : -1}
            data-index={index}
            onClick={() => onChange(option.value)}
            onKeyDown={onStarKeyDown}
          >
            <StarIcon filled={index <= selectedIndex} />
          </button>
        ))}
      </div>
      {selectedIndex >= 0 && <p className={styles.selectedLabel}>{options[selectedIndex].label}</p>}
    </QuestionWrapper>
  );
};

export default StarRatingQuestion;
